import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
  RadioGroup,
  Select,
  Switch,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { validateRangeRepeat } from '@/lib/domain/rangeRepeat';
import { REGION_AREA_NAME_MAX, validateRegionSurcharges } from '@/lib/domain/regionSurcharge';
import { CountryGroupEditor } from './CountryGroupEditor';
import {
  EMS_ADD_COST_UNITS,
  OVERSEAS_STORE_SCOPES,
  SHIPPING_BASES,
  isEmsZone,
  type EmsAddCostUnit,
  type OverseasCountryGroup,
  type OverseasShippingPolicy,
  type OverseasShippingPolicyPayload,
  type OverseasStoreScope,
  type ShippingBasis,
} from '../types/overseasShipping';

/** 국가코드 정규화 — 대문자 ISO2, 공백/빈값 제거, 중복 제거. be-order 가 raw 저장하므로 FE 가 정돈한다. */
const normalizeCountryCodes = (codes: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of codes) {
    const code = raw.trim().toUpperCase();
    if (code === '' || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
};

/**
 * EMS 추가요금 입력 해석. 빈 칸은 기본값 0 으로 본다.
 * 원(WON)은 0 이상 **정수**, %(PERCENT)는 0 이상 소수(예: 10.5). 규칙에 맞지 않으면 null.
 *
 * %는 소수 둘째 자리까지만 받는다 — BE 컬럼이 `DECIMAL(12,2)` 라 그보다 긴 값은 저장 시 조용히
 * 반올림된다. 입력한 값과 저장된 값이 달라지지 않게 여기서 막는다.
 */
export const parseEmsAddCost = (raw: string, unit: EmsAddCostUnit): number | null => {
  const v = raw.trim();
  if (v === '') return 0;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  if (unit === 'WON' && !Number.isInteger(n)) return null;
  if (unit === 'PERCENT' && !/^\d+(\.\d{1,2})?$/.test(v)) return null;
  return n;
};

/** 목록·요약용 표기 — "5,000원" / "10.5%". */
export const formatEmsAddCost = (value: number, unit: EmsAddCostUnit): string =>
  unit === 'WON' ? `${value.toLocaleString('ko-KR')}원` : `${value}%`;

interface OverseasPolicyEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 편집 대상. null 이면 신규. */
  target: OverseasShippingPolicy | null;
  saving: boolean;
  onSubmit: (payload: OverseasShippingPolicyPayload) => void;
}

interface EditorDraft {
  storeScope: OverseasStoreScope;
  basis: ShippingBasis;
  baseItemWeightKg: string;
  boxWeightKg: string;
  /** 빈 문자열 = 무제한(null). */
  maxWeightKg: string;
  /** EMS 추가요금 입력값(문자열 그대로 — 지우고 다시 칠 수 있게). 빈 칸 = 0. */
  emsAddCost: string;
  emsAddCostUnit: EmsAddCostUnit;
  insuranceRequired: boolean;
  insuranceAutoRange: string;
  active: boolean;
  countryGroups: OverseasCountryGroup[];
}

const emptyDraft = (): EditorDraft => ({
  storeScope: 'EN',
  basis: 'SELF',
  baseItemWeightKg: '0',
  boxWeightKg: '0',
  maxWeightKg: '',
  emsAddCost: '0',
  emsAddCostUnit: 'WON',
  insuranceRequired: false,
  insuranceAutoRange: '',
  active: true,
  countryGroups: [],
});

const toDraft = (target: OverseasShippingPolicy): EditorDraft => ({
  storeScope: target.storeScope,
  basis: target.basis,
  baseItemWeightKg: String(target.baseItemWeightKg),
  boxWeightKg: String(target.boxWeightKg),
  maxWeightKg: target.maxWeightKg == null ? '' : String(target.maxWeightKg),
  // V60 이전 응답에는 두 필드가 없다 — 기본값 0 · 원. BE 가 DECIMAL 이라 5000.00 처럼 올 수 있어
  // Number 로 한 번 거쳐 입력칸에 "5000" 으로 보이게 한다.
  emsAddCost: String(Number(target.emsAddCost ?? 0)),
  emsAddCostUnit: target.emsAddCostUnit ?? 'WON',
  insuranceRequired: target.insuranceRequired,
  insuranceAutoRange: target.insuranceAutoRange ?? '',
  active: target.active,
  countryGroups: target.countryGroups.map((g) => ({
    ...g,
    countryCodes: [...(g.countryCodes ?? [])],
    rates: g.rates.map((r) => ({ ...r })),
    regionSurcharges: (g.regionSurcharges ?? []).map((r) => ({ ...r })),
  })),
});

const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** 해외 배송 정책 생성/편집 다이얼로그. 무게/박스/기준/보험 + 국가그룹 동적테이블. */
export const OverseasPolicyEditor = ({
  open,
  onOpenChange,
  target,
  saving,
  onSubmit,
}: OverseasPolicyEditorProps) => {
  const { t } = useTranslation('overseasShipping');
  const [draft, setDraft] = useState<EditorDraft>(() => (target ? toDraft(target) : emptyDraft()));
  const [error, setError] = useState<string | null>(null);

  const storeOptions = OVERSEAS_STORE_SCOPES.map((s) => ({
    value: s,
    label: t(`overseasShipping.storeScope.${s}`),
  }));
  const emsAddCostValue = parseEmsAddCost(draft.emsAddCost, draft.emsAddCostUnit);

  const basisOptions = SHIPPING_BASES.map((b) => ({
    value: b,
    label: t(`overseasShipping.basis.${b}`),
  }));

  useEffect(() => {
    if (open) {
      setDraft(target ? toDraft(target) : emptyDraft());
      setError(null);
    }
  }, [open, target]);

  const set = <K extends keyof EditorDraft>(key: K, val: EditorDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = () => {
    setError(null);
    for (const g of draft.countryGroups) {
      if (g.groupName.trim() === '') {
        setError(t('overseasShipping.validation.groupNameRequired'));
        return;
      }
      if (draft.basis === 'EMS' && !isEmsZone(g.groupName.trim())) {
        setError(t('overseasShipping.validation.emsZoneRequired'));
        return;
      }
      // 범위반복은 자체요율에서만 쓰인다 — EMS 면 입력란이 숨어 있으므로 검사하지 않는다(값은 그대로 보낸다).
      if (draft.basis === 'SELF' && g.rangeRepeat) {
        const group = g.groupName.trim();
        if (g.rates.length === 0) {
          setError(t('overseasShipping.validation.rangeRepeatTierRequired', {
            group,
            defaultValue: '[{{group}}] 범위반복을 쓰려면 요율 구간이 1개 이상 있어야 합니다.',
          }));
          return;
        }
        const problem = validateRangeRepeat(g.rangeRepeat);
        if (problem) {
          const fallback = {
            start: '[{{group}}] 범위반복 시작값은 0 이상이어야 합니다.',
            unit: '[{{group}}] 범위반복 단위는 0 보다 커야 합니다.',
            fee: '[{{group}}] 범위반복 추가요금은 0 이상의 정수(원)여야 합니다.',
          }[problem];
          setError(t(`overseasShipping.validation.rangeRepeat_${problem}`, { group, defaultValue: fallback }));
          return;
        }
      }
      // 지역 추가배송비도 자체요율 전용 — 지역명이 매칭 기준이라 필수(be-order RegionSurchargeUpsert).
      if (draft.basis === 'SELF') {
        const problem = validateRegionSurcharges(
          (g.regionSurcharges ?? []).map((r) => ({ areaName: r.areaName, fee: r.surchargeKrw })),
        );
        if (problem) {
          const fallback = {
            nameRequired: '[{{group}}] 지역 추가배송비 {{row}}번째 행의 지역명을 입력하세요.',
            nameTooLong: '[{{group}}] 지역 추가배송비 {{row}}번째 행의 지역명은 {{max}}자 이하로 입력하세요.',
            fee: '[{{group}}] 지역 추가배송비 {{row}}번째 행의 추가 배송비는 0 이상의 정수(원)여야 합니다.',
          }[problem.kind];
          setError(t(`overseasShipping.validation.region_${problem.kind}`, {
            group: g.groupName.trim(),
            row: problem.index + 1,
            max: REGION_AREA_NAME_MAX,
            defaultValue: fallback,
          }));
          return;
        }
      }
    }
    // 자체요율(SELF)이면 입력란을 숨기므로 검증하지 않는다 — 보이지 않는 값 때문에 저장이 막히면 안 된다.
    if (draft.basis === 'EMS' && emsAddCostValue === null) {
      setError(
        draft.emsAddCostUnit === 'WON'
          ? t('overseasShipping.validation.emsAddCostAmount', {
              defaultValue: 'EMS 추가요금은 0 이상의 정수(원)로 입력하세요.',
            })
          : t('overseasShipping.validation.emsAddCostPercent', {
              defaultValue: 'EMS 추가요금은 0 이상, 소수 둘째 자리까지의 숫자(%)로 입력하세요. 예: 10.5',
            }),
      );
      return;
    }
    onSubmit({
      storeScope: draft.storeScope,
      basis: draft.basis,
      baseItemWeightKg: num(draft.baseItemWeightKg),
      boxWeightKg: num(draft.boxWeightKg),
      maxWeightKg: draft.maxWeightKg.trim() === '' ? null : num(draft.maxWeightKg),
      // SELF 에서도 보낸다(PUT 전체 교체 — 빼면 저장값이 지워진다). 숨겨진 값은 불러온 그대로라 유효하다.
      emsAddCost: emsAddCostValue ?? 0,
      emsAddCostUnit: draft.emsAddCostUnit,
      insuranceRequired: draft.insuranceRequired,
      insuranceAutoRange: draft.insuranceAutoRange.trim() === '' ? null : draft.insuranceAutoRange.trim(),
      active: draft.active,
      countryGroups: draft.countryGroups.map((g, i) => ({
        ...g,
        sortOrder: i,
        groupName: g.groupName.trim(),
        countryCodes: normalizeCountryCodes(g.countryCodes ?? []),
        // 범위반복·지역 추가배송비는 PUT 전체 교체 대상 — 빼면 지워지므로 항상 명시한다(없으면 null / 빈 배열).
        // EMS 로 저장하면 BE 는 둘 다(자체 요율과 함께) 저장하지 않는다 — 그룹을 지우고 다시 만들므로 기존 값도
        // 사라진다. 불러온 값을 그대로 보내는 건 무해해서일 뿐이고, 값이 남는 건 저장 전 편집 중에 자체요율로
        // 되돌릴 때뿐이다.
        rangeRepeat: g.rangeRepeat ?? null,
        regionSurcharges: (g.regionSurcharges ?? []).map((r) => ({
          areaName: r.areaName.trim(),
          surchargeKrw: r.surchargeKrw,
        })),
      })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            {target ? t('overseasShipping.editor.titleEdit') : t('overseasShipping.editor.titleCreate')}
          </DialogTitle>
          <DialogDescription>
            {t('overseasShipping.editor.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[64vh] flex-col gap-3 overflow-auto py-1">
          {error ? (
            <div
              className="rounded-md px-3 py-2 text-[13px]"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <FormSection
            label={t('overseasShipping.editor.store')}
            description={t('overseasShipping.editor.storeDesc')}
            density="compact"
            required
          >
            <RadioGroup
              value={draft.storeScope}
              onValueChange={(v) => set('storeScope', v as OverseasStoreScope)}
              options={storeOptions}
              disabled={saving}
              orientation="horizontal"
              aria-label={t('overseasShipping.editor.store')}
            />
          </FormSection>

          <FormSection
            label={t('overseasShipping.editor.basis')}
            description={t('overseasShipping.editor.basisDesc')}
            density="compact"
            required
          >
            <RadioGroup
              value={draft.basis}
              onValueChange={(v) => set('basis', v as ShippingBasis)}
              options={basisOptions}
              disabled={saving}
              orientation="horizontal"
              aria-label={t('overseasShipping.editor.basis')}
            />
          </FormSection>

          {/* 고도몰 해외배송 정책의 "EMS 추가요금" 이관. EMS 표준요율에 더해 청구되므로 기준 방식이
              EMS 일 때만 의미가 있다 — 자체요율(SELF)이면 숨긴다(값은 저장 시 그대로 보존). */}
          {draft.basis === 'EMS' ? (
            <FormSection
              label={t('overseasShipping.editor.emsAddCost', { defaultValue: 'EMS 추가요금' })}
              density="compact"
            >
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={draft.emsAddCostUnit === 'WON' ? 1 : 0.01}
                  value={draft.emsAddCost}
                  onChange={(e) => set('emsAddCost', e.target.value)}
                  disabled={saving}
                  className="max-w-[160px] tabular-nums"
                  aria-label={t('overseasShipping.editor.emsAddCost', { defaultValue: 'EMS 추가요금' })}
                />
                <Select
                  value={draft.emsAddCostUnit}
                  onChange={(e) => set('emsAddCostUnit', e.target.value as EmsAddCostUnit)}
                  disabled={saving}
                  className="max-w-[88px]"
                  aria-label={t('overseasShipping.editor.emsAddCostUnit', { defaultValue: 'EMS 추가요금 단위' })}
                >
                  {EMS_ADD_COST_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u === 'WON'
                        ? t('overseasShipping.emsAddCostUnit.WON', { defaultValue: '원' })
                        : t('overseasShipping.emsAddCostUnit.PERCENT', { defaultValue: '%' })}
                    </option>
                  ))}
                </Select>
                {/* 숫자 입력칸은 천 단위 구분을 못 하므로 읽기 쉬운 표기를 옆에 붙인다. */}
                {emsAddCostValue !== null && emsAddCostValue > 0 ? (
                  <span
                    className="text-[12px] tabular-nums"
                    style={{ color: 'var(--content-muted)' }}
                    data-testid="ems-add-cost-readout"
                  >
                    = {formatEmsAddCost(emsAddCostValue, draft.emsAddCostUnit)}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {t('overseasShipping.editor.emsAddCostHint', {
                  defaultValue: 'EMS 표준요율에 더해 청구됩니다. %는 표준요율 기준입니다.',
                })}
              </p>
            </FormSection>
          ) : null}

          <FormSection
            label={t('overseasShipping.editor.baseItemWeight')}
            description={t('overseasShipping.editor.baseItemWeightDesc')}
            density="compact"
          >
            <Input
              type="number"
              step="0.01"
              min={0}
              value={draft.baseItemWeightKg}
              onChange={(e) => set('baseItemWeightKg', e.target.value)}
              disabled={saving}
              className="max-w-[160px] tabular-nums"
            />
          </FormSection>

          <FormSection
            label={t('overseasShipping.editor.boxWeight')}
            description={t('overseasShipping.editor.boxWeightDesc')}
            density="compact"
          >
            <Input
              type="number"
              step="0.01"
              min={0}
              value={draft.boxWeightKg}
              onChange={(e) => set('boxWeightKg', e.target.value)}
              disabled={saving}
              className="max-w-[160px] tabular-nums"
            />
          </FormSection>

          <FormSection label={t('overseasShipping.editor.maxWeight')} density="compact">
            <Input
              type="number"
              step="0.01"
              min={0}
              value={draft.maxWeightKg}
              onChange={(e) => set('maxWeightKg', e.target.value)}
              disabled={saving}
              placeholder={t('overseasShipping.editor.maxWeightPlaceholder')}
              className="max-w-[160px] tabular-nums"
              aria-label={t('overseasShipping.editor.maxWeight')}
            />
            <p className="mt-1 text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {t('overseasShipping.editor.maxWeightHint')}
            </p>
          </FormSection>

          <FormSection
            label={t('overseasShipping.editor.insuranceRequired')}
            description={t('overseasShipping.editor.insuranceRequiredDesc')}
            density="compact"
          >
            <Switch
              checked={draft.insuranceRequired}
              onCheckedChange={(v) => set('insuranceRequired', v)}
              disabled={saving}
              aria-label={t('overseasShipping.editor.insuranceRequired')}
            />
          </FormSection>

          <FormSection
            label={t('overseasShipping.editor.insuranceAutoRange')}
            description={t('overseasShipping.editor.insuranceAutoRangeDesc')}
            density="compact"
          >
            <Input
              value={draft.insuranceAutoRange}
              onChange={(e) => set('insuranceAutoRange', e.target.value)}
              placeholder={t('overseasShipping.editor.insuranceAutoRangePlaceholder')}
              disabled={saving}
            />
          </FormSection>

          <FormSection
            label={t('overseasShipping.editor.active')}
            description={t('overseasShipping.editor.activeDesc')}
            density="compact"
          >
            <Switch
              checked={draft.active}
              onCheckedChange={(v) => set('active', v)}
              disabled={saving}
              aria-label={t('overseasShipping.editor.active')}
            />
          </FormSection>

          <div className="mt-1">
            <p className="mb-1 text-[13px] font-semibold" style={{ color: 'var(--content-strong)' }}>
              {t('overseasShipping.editor.countryGroupsTitle')}
            </p>
            <p className="mb-2 text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {t('overseasShipping.editor.countryGroupsDesc')}
            </p>
            <CountryGroupEditor
              groups={draft.countryGroups}
              basis={draft.basis}
              disabled={saving}
              onChange={(groups) => set('countryGroups', groups)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('overseasShipping.editor.cancel')}
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="overseas-save">
            {saving ? t('overseasShipping.editor.saving') : t('overseasShipping.editor.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
